/**
 * Loans admin API client — pcb-loans-api v3/loans/admin/* (LOP-owned; LoansAdminV3Functions, 0.137.0+).
 * Same transport as the Hub client: ONLY the shared backend token (auth/tokens.ts). Until consent exists every call rejects with
 * ConsentWaitingError and no request is made. Authority is the API's own (loans.AdminGrant on the signed-in account); a
 * non-admin gets 403. Every write carries x-pcb-change-reason and lands in loans.AdminAudit.
 *
 * Pages under src/platforms/loans/** (LWP's writeups/ pages included) call loansApi.get / loansApi.send — never fetch directly.
 */

import { getPlatformToken } from '@/auth/tokens';
import { APP_VERSION } from '@/constants/version';
import { ConsentWaitingError } from '@/platforms/hub/hubApi';
import type { Capabilities } from '@/platforms/types';

export const LOANS_API_BASE =
  ((import.meta.env.VITE_LOANS_API_BASE_URL as string | undefined) ?? '').replace(/\/$/, '') ||
  'https://pcb-loans-api.azurewebsites.net/api';

export class LoansApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'LoansApiError';
    this.status = status;
    this.code = code;
  }
}

type Method = 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE';

interface CallOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  /** x-pcb-change-reason — required by pcb-loans-api on every admin write. Printable ASCII. */
  reason?: string;
}

async function call<T>(method: Method, path: string, opts: CallOptions = {}): Promise<T> {
  const token = await getPlatformToken();
  if (!token) throw new ConsentWaitingError();

  const url = new URL(`${LOANS_API_BASE}/${path.replace(/^\//, '')}`);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'x-pcb-client-id': `admin-panel@${APP_VERSION}`,
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.reason !== undefined) headers['x-pcb-change-reason'] = opts.reason;

  let resp: Response;
  try {
    resp = await fetch(url.toString(), {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      cache: 'no-store',
    });
  } catch (e) {
    throw new LoansApiError(0, `Could not reach the loans API: ${e instanceof Error ? e.message : String(e)}`);
  }
  let json: unknown = null;
  try {
    json = await resp.json();
  } catch {
    json = null;
  }
  const obj = (json && typeof json === 'object' ? json : {}) as { success?: boolean; ok?: boolean; code?: string; error?: string; message?: string };
  if (!resp.ok || obj.success === false || obj.ok === false) {
    throw new LoansApiError(resp.status, obj.error ?? obj.message ?? `The loans API answered ${resp.status}.`, obj.code);
  }
  return json as T;
}

export interface LoansCapabilities extends Capabilities {
  account?: string;
  domains?: string[];
}

export const loansApi = {
  capabilities: () => call<LoansCapabilities>('GET', 'v3/loans/admin/capabilities'),
  /** A read under v3/loans/admin/* (or any bearer route of pcb-loans-api). */
  get: <T>(path: string, query?: CallOptions['query']) => call<T>('GET', path, { query }),
  /** A write: the reason is mandatory and is what the audit shows. */
  send: <T>(method: Exclude<Method, 'GET'>, path: string, body: unknown, reason: string) => call<T>(method, path, { body, reason }),
};
